/* eslint-disable no-console */
import {Col, Form, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {useContext, useEffect, useMemo, useState} from 'react';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {CommonErrors, TF} from '../../constants/Common';
import FilteredUnit from '../../constants/FilteredUnit';
import InformativeText from '../../constants/InformativeText';
import {Itabs} from '../../interface/Tab';
import TranslatedText from '../../libraries/I18N/TranslatedText';
import SelectedCountryContext from '../../pages/configurator/orgFacility/Constants/SelectedCountryContext';
import {sortYearOptions} from '../../pages/orgManager/Utils/InfraStructureProfile/constant/sorYear';
import yearFilterArray from '../../shared/YearFilterArray';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import useUserStore from '../../store/userStore/userStore';
import {hasUserRole, UserRole} from '../../utils/commonFunctions';
import {MYConfig, MYData} from '../MonthlyYearly/Interface/common';
import MonthlyYearlyDrawerForm from '../MonthlyYearly/MonthlyYearlyDrawerForm';
import Tabs from '../Tabs/Tabs';

import {facilityKeyLabel, yearKeyLabel} from './Constants/IP/commonKeyLabel';
import {WasteKey, WasteLabel} from './Constants/IP/WasteKeyLabel';
import {unitType, unitTypes} from './interface/unitChange';
import {getUnits} from './Utils/changeUnit';
import {filterSelectOptions} from './Utils/commonFunctions';

const WasteForm: React.FC<{currentStep?: number}> = ({currentStep}) => {
  const isCompanyUserOrAdmin =
    hasUserRole(UserRole.Company_Admin) || hasUserRole(UserRole.Company_user);
  const {getFieldValue, setFields} = useFormInstance();
  const userDetails = useUserStore.getState().userDetails;
  const {wasteCategory, wasteType} = useJSONStore.getState();
  const {efdbData, facilityId, selectedYear, selectedCountry} = useContext(
    SelectedCountryContext
  );

  const wasteInfo = InformativeText?.IP?.waste;
  const [wasteTypeSelected, setWasteType] = useState<unitType>(
    wasteType?.find((item) => {
      return item?.value === getFieldValue(WasteKey.wasteType);
    })?.state ?? unitTypes.WEIGHT
  );

  const facilityDetails = useCompanyStore.getState().facilityDetails;
  const [formSelectedCountry, setFormSelectedCountry] = useState<string>('');
  const [facilityOpt, setFacilityOpt] = useState<any[]>([]);
  const [facilityEfdb, setFacilityEfdb] = useState<any>();

  useEffect(() => {
    setFormSelectedCountry(
      facilityOpt?.find((item: any) => {
        return (
          item?._id === (getFieldValue(facilityKeyLabel.key) ?? facilityId)
        );
      })?.country
    );
  }, [facilityOpt]);

  useEffect(() => {
    setFacilityOpt(
      facilityDetails?.[getFieldValue(yearKeyLabel.key) ?? selectedYear] ?? []
    );
    setFacilityEfdb(
      efdbData?.[getFieldValue(yearKeyLabel.key) ?? selectedYear]?.find(
        (i: any) => {
          return (
            i.facilityId === (getFieldValue(facilityKeyLabel.key) ?? facilityId)
          );
        }
      )
    );
  }, []);

  const [yearOptions, setYearOptions] = useState<any[]>([]);

  useEffect(() => {
    setYearOptions(
      yearFilterArray(
        facilityDetails,
        !isCompanyUserOrAdmin ? userDetails?.facility : undefined
      )
    );
  }, []);

  const monYear: {
    main: MYConfig[];
    treated: MYConfig[];
    notTreated: MYConfig[];
  } = useMemo(() => {
    return {
      main: [
        {
          mainTitle: {
            key: WasteKey.wasteProduced,
            title: WasteLabel.wasteProduced
          },
          unit: getUnits(
            wasteTypeSelected,
            FilteredUnit(formSelectedCountry ?? selectedCountry)
          )
        }
      ],
      treated: [
        {
          mainTitle: {
            key: WasteKey.treated.wasteTreated,
            title: WasteLabel.treated.wasteTreated
          },
          unit: getUnits(
            wasteTypeSelected,
            FilteredUnit(formSelectedCountry ?? selectedCountry)
          )
        },
        {
          mainTitle: {
            key: WasteKey.treated.treatmentCost,
            title: WasteLabel.treated.treatmentCost
          },
          unit: FilteredUnit(formSelectedCountry ?? selectedCountry).currency
        }
      ],
      notTreated: [
        {
          mainTitle: {
            key: WasteKey.notTreated.wasteNotTreated,
            title: WasteLabel.notTreated.wasteNotTreated
          },
          unit: getUnits(
            wasteTypeSelected,
            FilteredUnit(formSelectedCountry ?? selectedCountry)
          )
        }
      ]
    };
  }, [formSelectedCountry, selectedCountry, wasteTypeSelected]);

  const wasteMethod: Itabs[] = useMemo(() => {
    return [
      {
        id: WasteKey.disposalKey.landfill,
        name: (
          <TranslatedText
            translationKey={WasteLabel.disposalKey.landfill}
            tooltip={wasteInfo?.landfill}
          />
        ),
        component: (
          <MonthlyYearlyDrawerForm
            config={monYear?.treated}
            data={
              getFieldValue(WasteKey.disposalMethodData)?.find((item: any) => {
                return item?.disposalMethod === WasteKey.disposalKey.landfill;
              })?.[TF]
            }
            disposalMethod={{
              value: true,
              type: WasteKey.disposalKey.landfill,
              key: WasteKey.disposalMethod
            }}
            nested={WasteKey.disposalMethodData}
          />
        ),
        forceRender: true
      },
      {
        id: WasteKey.disposalKey.incineration,
        name: (
          <TranslatedText
            translationKey={WasteLabel.disposalKey.incineration}
            tooltip={wasteInfo?.incineration}
          />
        ),
        component: (
          <MonthlyYearlyDrawerForm
            config={monYear?.treated}
            data={
              (getFieldValue(WasteKey.disposalMethodData) ?? [])?.find(
                (item: any) => {
                  return (
                    item?.disposalMethod === WasteKey.disposalKey.incineration
                  );
                }
              )?.[TF]
            }
            disposalMethod={{
              value: true,
              type: WasteKey.disposalKey.incineration,
              key: WasteKey.disposalMethod
            }}
            nested={WasteKey.disposalMethodData}
          />
        ),
        forceRender: true
      },
      {
        id: WasteKey.disposalKey.recycling,
        name: (
          <TranslatedText
            translationKey={WasteLabel.disposalKey.recycling}
            tooltip={wasteInfo?.recycle}
          />
        ),
        component: (
          <MonthlyYearlyDrawerForm
            config={monYear?.treated}
            data={
              (getFieldValue(WasteKey.disposalMethodData) ?? [])?.find(
                (item: any) => {
                  return (
                    item?.disposalMethod === WasteKey.disposalKey.recycling
                  );
                }
              )?.[TF]
            }
            disposalMethod={{
              value: true,
              type: WasteKey.disposalKey.recycling,
              key: WasteKey.disposalMethod
            }}
            nested={WasteKey.disposalMethodData}
          />
        ),
        forceRender: true
      },
      {
        id: WasteKey.disposalKey.reclaimed,
        name: (
          <TranslatedText
            translationKey={WasteLabel.disposalKey.reclaimed}
            tooltip={wasteInfo?.reclaimed}
          />
        ),
        component: (
          <MonthlyYearlyDrawerForm
            config={monYear?.treated}
            data={
              (getFieldValue(WasteKey.disposalMethodData) ?? [])?.find(
                (item: any) => {
                  return (
                    item?.disposalMethod === WasteKey.disposalKey.reclaimed
                  );
                }
              )?.[TF]
            }
            disposalMethod={{
              value: true,
              type: WasteKey.disposalKey.reclaimed,
              key: WasteKey.disposalMethod
            }}
            nested={WasteKey.disposalMethodData}
          />
        ),
        forceRender: true
      },
      {
        id: WasteKey.disposalKey.reused,
        name: (
          <TranslatedText
            translationKey={WasteLabel.disposalKey.reused}
            tooltip={wasteInfo?.reuse}
          />
        ),
        component: (
          <MonthlyYearlyDrawerForm
            config={monYear?.treated}
            data={
              (getFieldValue(WasteKey.disposalMethodData) ?? [])?.find(
                (item: any) => {
                  return item?.disposalMethod === WasteKey.disposalKey.reused;
                }
              )?.[TF]
            }
            disposalMethod={{
              value: true,
              type: WasteKey.disposalKey.reused,
              key: WasteKey.disposalMethod
            }}
            nested={WasteKey.disposalMethodData}
          />
        ),
        forceRender: true
      },
      {
        id: WasteKey.disposalKey.notTreated,
        name: (
          <TranslatedText translationKey={WasteLabel.disposalKey.notTreated} />
        ),
        component: (
          <MonthlyYearlyDrawerForm
            config={monYear?.notTreated}
            data={
              (getFieldValue(WasteKey.disposalMethodData) ?? [])?.find(
                (item: any) => {
                  return (
                    item?.disposalMethod === WasteKey.disposalKey.notTreated
                  );
                }
              )?.[TF]
            }
            disposalMethod={{
              value: true,
              type: WasteKey.disposalKey.notTreated,
              key: WasteKey.disposalMethod
            }}
            nested={WasteKey.disposalMethodData}
          />
        ),
        forceRender: true
      }
    ];
  }, [monYear]);

  return (
    <>
      {currentStep === 0 && (
        <>
          <Col className="gutter-row">
            <Form.Item
              name={yearKeyLabel.key}
              label={yearKeyLabel.label}
              labelCol={{span: 24}}
              initialValue={selectedYear}
            >
              <Select
                placeholder={yearKeyLabel.label}
                options={sortYearOptions(yearOptions)}
                suffixIcon={<YearCalender />}
                disabled={getFieldValue('_id') !== undefined}
                onChange={(e) => {
                  setFacilityEfdb(() => {
                    return efdbData?.[e]?.find((i: any) => {
                      return (
                        i?.facilityId === getFieldValue(facilityKeyLabel.key)
                      );
                    });
                  });
                  setFacilityOpt(facilityDetails?.[e] ?? []);
                  if (getFieldValue(facilityKeyLabel.key)) {
                    setFields([
                      {
                        name: facilityKeyLabel.key,
                        value: undefined,
                        errors: ['Please select facility again']
                      }
                    ]);
                  }
                }}
              />
            </Form.Item>
          </Col>
          {isCompanyUserOrAdmin && (
            <>
              <Col className="gutter-row">
                <Form.Item
                  name={facilityKeyLabel.key}
                  label={facilityKeyLabel.label}
                  labelCol={{span: 24}}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(facilityKeyLabel.label)
                    }
                  ]}
                  initialValue={facilityId}
                >
                  <Select
                    placeholder={facilityKeyLabel.label}
                    options={
                      facilityOpt?.map((item: any) => {
                        return {label: item.name, value: item._id};
                      }) ?? []
                    }
                    disabled={getFieldValue('_id') !== undefined}
                    onChange={(e) => {
                      setFacilityEfdb(() => {
                        return efdbData?.[
                          getFieldValue(yearKeyLabel.key)
                        ]?.find((i: any) => {
                          return i?.facilityId === e;
                        });
                      });
                      if (getFieldValue(WasteKey.wasteType)) {
                        setFields([
                          {
                            name: WasteKey.wasteType,
                            errors: [CommonErrors.selectAgain],
                            value: undefined
                          }
                        ]);
                      }
                      setFormSelectedCountry(
                        facilityDetails?.[
                          getFieldValue(yearKeyLabel.key) ?? ''
                        ]?.find((item: any) => {
                          return item._id === e;
                        })?.country
                      );
                    }}
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </>
      )}
      {currentStep === 1 && (
        <>
          <Col className="gutter-row">
            <Form.Item
              name={WasteKey.wasteCategory}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(WasteLabel.wasteCategory)
                }
              ]}
              label={WasteLabel.wasteCategory}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
            >
              <Select
                showSearch
                filterOption={filterSelectOptions}
                className="select"
                placeholder={`Choose ${WasteLabel.wasteCategory}`}
                options={wasteCategory}
                onChange={() => {
                  if (getFieldValue(WasteKey.wasteType)) {
                    setFields([
                      {
                        name: WasteKey.wasteType,
                        errors: [CommonErrors.selectAgain],
                        value: undefined
                      }
                    ]);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              dependencies={[
                WasteKey.wasteCategory,
                facilityKeyLabel.key,
                yearKeyLabel.key
              ]}
              noStyle
            >
              {({getFieldValue}) => {
                return (
                  <Form.Item
                    name={WasteKey.wasteType}
                    rules={[
                      {
                        required: true,
                        message: CommonErrors.mandatory(WasteLabel.wasteType)
                      }
                    ]}
                    label={WasteLabel.wasteType}
                    labelCol={{span: 24}}
                    validateFirst
                    hasFeedback
                  >
                    <Select
                      showSearch
                      filterOption={filterSelectOptions}
                      className="select"
                      placeholder={WasteLabel.wasteType}
                      options={
                        getFieldValue(WasteKey.wasteCategory) !== undefined
                          ? wasteType?.filter((item) => {
                              return (
                                item.typeOfWasteId ===
                                  getFieldValue(WasteKey.wasteCategory) &&
                                item?.dbName === facilityEfdb?.scope3
                              );
                            })
                          : []
                      }
                      onChange={(e, value) => {
                        setWasteType(value.state);
                      }}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
          <MonthlyYearlyDrawerForm
            config={monYear?.main}
            data={getFieldValue(TF) as MYData[]}
          />
        </>
      )}

      {currentStep === 2 && (
        <>
          <Tabs array={wasteMethod} type="card" destroy={false} slide={true} />
        </>
      )}
    </>
  );
};

export default WasteForm;
